import React, { useEffect, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import { SUB_TYPE_OPTIONS, hasActiveFilters, subTypeLabel } from '../config';

const SEARCH_DEBOUNCE_MS = 350;

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

const Chip = ({ children, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 py-1 pl-2.5 pr-1.5 text-xs font-medium text-sky-800 ring-1 ring-inset ring-sky-200">
    {children}
    <button
      type="button"
      onClick={onRemove}
      aria-label="Remove filter"
      className="flex h-4 w-4 items-center justify-center rounded-full text-sky-700 transition hover:bg-sky-200"
    >
      <SlIcon library="lucide" name="x" style={{ fontSize: '11px' }} />
    </button>
  </span>
);

/**
 * Filter bar for the creative list.
 *
 * The search box holds its own draft value and only commits it upstream after
 * a 350ms pause — the committed value is what ends up in the filters object
 * (and therefore in the request), so typing never fires a request per keypress.
 */
const FilterBar = ({ filters, onChange, onClear, disabled = false }) => {
  const [text, setText] = useState(filters.query || '');
  const [mobileOpen, setMobileOpen] = useState(false);

  const committedRef = useRef(filters.query || '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Adopt an externally driven query change (Clear all, restored state, …).
  useEffect(() => {
    const incoming = filters.query || '';
    if (incoming !== committedRef.current) {
      committedRef.current = incoming;
      setText(incoming);
    }
  }, [filters.query]);

  useEffect(() => {
    if (text === committedRef.current) return undefined;
    const timer = setTimeout(() => {
      committedRef.current = text;
      onChangeRef.current({ query: text });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const set = (patch) => onChangeRef.current(patch);
  const active = hasActiveFilters(filters);

  // Rendered twice (desktop inline + mobile sheet), so ids need a scope to
  // stay unique in the DOM.
  const controls = (scope) => (
    <>
      <div className="w-full sm:w-52">
        <label className={labelCls} htmlFor={`${scope}-sub-type`}>
          Sub type
        </label>
        <select
          id={`${scope}-sub-type`}
          className={inputCls}
          value={filters.sub_type || ''}
          disabled={disabled}
          onChange={(e) => set({ sub_type: e.target.value })}
        >
          <option value="">All sub types</option>
          {SUB_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="w-full sm:w-40">
        <label className={labelCls} htmlFor={`${scope}-start-date`}>
          Created from
        </label>
        <input
          id={`${scope}-start-date`}
          type="date"
          className={inputCls}
          value={filters.start_date || ''}
          max={filters.end_date || undefined}
          disabled={disabled}
          onChange={(e) => set({ start_date: e.target.value })}
        />
      </div>

      <div className="w-full sm:w-40">
        <label className={labelCls} htmlFor={`${scope}-end-date`}>
          Created to
        </label>
        <input
          id={`${scope}-end-date`}
          type="date"
          className={inputCls}
          value={filters.end_date || ''}
          min={filters.start_date || undefined}
          disabled={disabled}
          onChange={(e) => set({ end_date: e.target.value })}
        />
      </div>

      <div className="w-full sm:w-auto">
        <span className={labelCls}>Sort</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => set({ sort_by_latest_activity: !filters.sort_by_latest_activity })}
          aria-pressed={!!filters.sort_by_latest_activity}
          className={`inline-flex h-[38px] w-full items-center gap-2 rounded-lg border px-3 text-sm font-medium transition sm:w-auto ${
            filters.sort_by_latest_activity
              ? 'border-sky-300 bg-sky-50 text-sky-800'
              : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <span
            className={`relative h-4 w-8 shrink-0 rounded-full transition-colors ${
              filters.sort_by_latest_activity ? 'bg-sky-600' : 'bg-slate-300'
            }`}
          >
            <span
              className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                filters.sort_by_latest_activity ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </span>
          Latest activity
        </button>
      </div>
    </>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[180px] flex-1">
          <label className={labelCls} htmlFor="ob-search">
            Search
          </label>
          <div className="relative">
            <SlIcon
              library="lucide"
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              style={{ fontSize: '15px' }}
            />
            <input
              id="ob-search"
              type="search"
              className={`${inputCls} pl-9`}
              placeholder="Search by creative name"
              value={text}
              disabled={disabled}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
        </div>

        {/* Desktop-only inline controls. `max-md:hidden`, never `hidden md:block`. */}
        <div className="max-md:hidden flex flex-wrap items-end gap-3">{controls('cw-d')}</div>

        {/* Mobile-only disclosure trigger */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          className="md:hidden inline-flex h-[38px] items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700"
        >
          <SlIcon library="lucide" name="sliders-horizontal" style={{ fontSize: '15px' }} />
          Filters
          <SlIcon
            library="lucide"
            name={mobileOpen ? 'chevron-up' : 'chevron-down'}
            style={{ fontSize: '14px' }}
          />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          {controls('cw-m')}
        </div>
      )}

      {active && (
        <div className="flex flex-wrap items-center gap-2">
          {filters.query ? (
            <Chip onRemove={() => set({ query: '' })}>“{filters.query}”</Chip>
          ) : null}
          {filters.sub_type ? (
            <Chip onRemove={() => set({ sub_type: '' })}>{subTypeLabel(filters.sub_type)}</Chip>
          ) : null}
          {filters.start_date ? (
            <Chip onRemove={() => set({ start_date: '' })}>From {filters.start_date}</Chip>
          ) : null}
          {filters.end_date ? (
            <Chip onRemove={() => set({ end_date: '' })}>To {filters.end_date}</Chip>
          ) : null}
          {filters.sort_by_latest_activity ? (
            <Chip onRemove={() => set({ sort_by_latest_activity: false })}>
              Sorted by latest activity
            </Chip>
          ) : null}
          <button
            type="button"
            onClick={onClear}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 underline-offset-2 transition hover:text-slate-800 hover:underline"
          >
            <SlIcon library="lucide" name="eraser" style={{ fontSize: '13px' }} />
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
